import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import {lighten} from '@material-ui/core/styles/colorManipulator';

window.__MUI_USE_NEXT_TYPOGRAPHY_VARIANTS__ = true;

const rows = [
    {id: 0, key: 'id', label: 'id', width: '0%', disablePadding: true},
    {id: 1, key: 'activeSubstance1', label: 'Active Substance', width: '17%', disablePadding: true},
    {id: 2, key: 'atcCode', label: 'ATC Code', width: '8%', disablePadding: true},
    {id: 3, key: 'brandName', label: 'Brand Name', width: '12%', disablePadding: true},
    {id: 4, key: 'costPerUnit', label: 'Cost Per Unit', width: '10%', disablePadding: true},
    {id: 5, key: 'countryCode', label: 'Country Code', width: '13%', disablePadding: true},
    {id: 6, key: 'dosageForm', label: 'Dosage Form', width: '10%', disablePadding: true},
    {id: 7, key: 'genericName1', label: 'Generic Name', width: '15%', disablePadding: true},
    {id: 8, key: 'manufacturer1', label: 'Manufacturer', width: '15%', disablePadding: true}
];

class EnhancedTableHead extends React.Component {
    createSortHandler = property => event => {
        this.props.onRequestSort(event, property);
    };

    render() {
        const {order, orderBy} = this.props;
        return (
            <TableHead>
                <TableRow>
                    {rows.filter(row => row.key !== 'id').map(row => {
                        return (
                            <TableCell
                                style={{width: row.width}}
                                key={row.id}
                                numeric={row.numeric}
                                padding={row.disablePadding ? 'none' : 'default'}
                                sortDirection={orderBy === row.key ? order : false}
                            >
                                <Tooltip
                                    title="Sort"
                                    placement={row.numeric ? 'bottom-end' : 'bottom-start'}
                                    enterDelay={300}
                                >
                                    <TableSortLabel
                                        active={orderBy === row.key}
                                        direction={order}
                                        onClick={this.createSortHandler(row.key)}
                                    >
                                        {row.label}
                                    </TableSortLabel>
                                </Tooltip>
                            </TableCell>
                        );
                    }, this)}
                </TableRow>
            </TableHead>
        );
    }
}

EnhancedTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.string.isRequired,
    orderBy: PropTypes.any.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const toolbarStyles = theme => ({
    root: {
        paddingRight: theme.spacing.unit,
    },
    highlight:
        theme.palette.type === 'light'
            ? {
                color: theme.palette.secondary.main,
                backgroundColor: lighten(theme.palette.secondary.light, 0.85),
            }
            : {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.secondary.dark,
            },
    spacer: {
        flex: '1 1 100%',
    },
    actions: {
        color: theme.palette.text.secondary,
    },
    title: {
        flex: '0 0 auto',
    },
});

let EnhancedTableToolbar = props => {
    const {classes} = props;

    return (
        <Toolbar
            className={classNames(classes.root)}
        >
            <div className={classes.title}>
                <Typography variant="h6" id="tableTitle">
                    Drugs
                </Typography>
            </div>
            <div className={classes.spacer}/>
        </Toolbar>
    );
};

EnhancedTableToolbar.propTypes = {
    classes: PropTypes.object.isRequired
};

EnhancedTableToolbar = withStyles(toolbarStyles)(EnhancedTableToolbar);

const styles = theme => ({
    root: {
        width: '100%',
        paddingLeft: '20px'
    },
    table: {
        minWidth: 1020,
    },
    tableWrapper: {
        overflowX: 'auto',
    },
});

class EnhancedTable extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            rowsPerPage: this.props.rowsPerPage | 0
        }
    }

    handleRequestSort = (event, property) => {
        let order = 'desc';

        if (this.props.orderBy === property && this.props.order === 'desc') {
            order = 'asc';
        }
        this.props.onSort(property, order);
    };

    handleChangePage = (event, page) => {
        this.props.onPageChange(page);
    };

    handleChangeRowsPerPage = event => {
        this.setState(() => {
            return {
                ...this.state,
                rowsPerPage: event.target.value
            };
        });
        this.props.onPageSizeChange(event.target.value);
    };

    handleRowClick = (id) => {
        this.props.history.push("/drugs/" + id);
    };

    render() {
        const {classes, order, orderBy, rowsPerPage, page} = this.props;
        const data = this.props.data;
        const rowCount = this.props.rowCount | 0;
        return (
            <Paper className={classes.root}>
                <EnhancedTableToolbar/>
                <div className={classes.tableWrapper}>
                    <Table className={classes.table} aria-labelledby="tableTitle">
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={this.handleRequestSort}
                            rowCount={data.length}
                        />
                        <TableBody>
                            {data
                                .map(n => {
                                    return (
                                        <TableRow
                                            style={{cursor: 'pointer'}}
                                            onClick={() => this.handleRowClick(n.drug_id)}
                                            hover
                                            tabIndex={-1}
                                            key={n.id}
                                        >

                                            <TableCell component="th" scope="row" padding="none">
                                                {n.activeSubstance1}
                                            </TableCell>
                                            <TableCell component="th" scope="row" padding="none">{n.atcCode}</TableCell>
                                            <TableCell component="th" scope="row"
                                                       padding="none">{n.brandName}</TableCell>
                                            <TableCell component="th" scope="row"
                                                       padding="none">{n.costPerUnit}</TableCell>
                                            <TableCell component="th" scope="row"
                                                       padding="none">{n.countryCode}</TableCell>
                                            <TableCell component="th" scope="row"
                                                       padding="none">{n.dosageForm}</TableCell>
                                            <TableCell component="th" scope="row"
                                                       padding="none">{n.genericName1}</TableCell>
                                            <TableCell component="th" scope="row"
                                                       padding="none">{n.manufacturer1}</TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </div>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={rowCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    backIconButtonProps={{
                        'aria-label': 'Previous Page',
                    }}
                    nextIconButtonProps={{
                        'aria-label': 'Next Page',
                    }}
                    onChangePage={this.handleChangePage}
                    onChangeRowsPerPage={this.handleChangeRowsPerPage}
                />
            </Paper>
        );
    }
}

EnhancedTable.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(EnhancedTable);
